
import React from "react";
import { useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { formatDecimalPace } from "../../utils/formatWorkout";
import {rpeEmoji} from "../../utils/rpe";

function computeDailySummary(rows) {
  const validRows = rows?.filter(row => row != null && typeof row === 'object') || [];
  if (!validRows.length) return null;

  let plannedSum = 0, plannedCount = 0;
  let actualDistanceSum = 0;
  let actualPaceWeightedSum = 0;
  let hrSum = 0, hrCount = 0;
  let maxHr = null;
  let elevationSum = 0, elevationCount = 0;
  let workPaceSum = 0, workPaceCount = 0;
  let workHrSum = 0, workHrCount = 0;
  let steadyPaceSum = 0, steadyPaceCount = 0;
  let steadyHrSum = 0, steadyHrCount = 0;
  let supplementarySum = 0;
  let rpeSum = 0, rpeCount = 0;

  validRows.forEach((row) => {
    if (row.planned?.distance_km != null) {
      plannedSum += row.planned.distance_km;
      plannedCount++;
    }
    if (row.actual?.distance_km != null) {
      actualDistanceSum += row.actual.distance_km;
      if (row.actual.avg_pace != null) {
        actualPaceWeightedSum += row.actual.avg_pace * row.actual.distance_km;
      }
    }
    if (row.actual?.avg_hr != null) {
      hrSum += row.actual.avg_hr;
      hrCount++;
    }
    if (row.actual?.max_hr != null) {
      maxHr = maxHr == null ? row.actual.max_hr : Math.max(maxHr, row.actual.max_hr);
    }
    if (row.actual?.elevation_gain_m != null) {
      elevationSum += row.actual.elevation_gain_m;
      elevationCount++;
    }
    if (row.work_summary?.avg_pace != null) {
      workPaceSum += row.work_summary.avg_pace;
      workPaceCount++;
    }
    if (row.work_summary?.avg_hr != null) {
      workHrSum += row.work_summary.avg_hr;
      workHrCount++;
    }
    if (row.steady_summary?.avg_pace != null) {
      steadyPaceSum += row.steady_summary.avg_pace;
      steadyPaceCount++;
    }
    if (row.steady_summary?.avg_hr != null) {
      steadyHrSum += row.steady_summary.avg_hr;
      steadyHrCount++;
    }
    if (row.rpe != null) {
      rpeSum += row.rpe;
      rpeCount++;
    }
    if (row.supplementary_activities?.length > 0) {
      row.supplementary_activities.forEach((s) => {
        if (s.distance_km != null) supplementarySum += s.distance_km;
      });
    }
  });

  const totalActualWithSupplementary = actualDistanceSum + supplementarySum;

  return {
    plannedSum: plannedCount ? Math.round(plannedSum * 10) / 10 : null,
    actualDistanceSum: actualDistanceSum > 0 ? Math.round(actualDistanceSum * 10) / 10 : null,
    supplementarySum: supplementarySum > 0 ? Math.round(supplementarySum * 10) / 10 : null,
    totalActualWithSupplementary: totalActualWithSupplementary > 0 ? Math.round(totalActualWithSupplementary * 10) / 10 : null,
    actualAvgPace: actualDistanceSum > 0 ? actualPaceWeightedSum / actualDistanceSum : null,
    avgHr: hrCount ? hrSum / hrCount : null,
    maxHr,
    elevationSum: elevationCount ? Math.round(elevationSum) : null,
    workPace: workPaceCount ? workPaceSum / workPaceCount : null,
    workHr: workHrCount ? workHrSum / workHrCount : null,
    steadyPace: steadyPaceCount ? steadyPaceSum / steadyPaceCount : null,
    avgRpe: rpeCount ? rpeSum / rpeCount : null,
    steadyHr: steadyHrCount ? steadyHrSum / steadyHrCount : null,
  };
}

const getWorkoutColor = (type) => {
  const map = {
    race: 'text-red-700 font-semibold',
    threshold: 'text-amber-600 font-semibold',
    short_reps: 'text-amber-600 font-semibold',
    long_run: 'text-blue-600 font-semibold',
    long_intervals: 'text-purple-600 font-semibold',
    medium_intervals: 'text-purple-600 font-semibold',
    short_intervals: 'text-purple-600 font-semibold',
    easy: 'text-green-600',
    rest: 'text-gray-400',
    assessment: 'text-yellow-600',
    prerace_intervals: 'text-indigo-600',
  };
  return map[type] || 'text-gray-700';
};

export const DailyTable = ({ rows, t, selectedDates, onToggleSelect }) => {
  const navigate = useNavigate();
  const validRows = rows?.filter(row => row != null && typeof row === 'object') || [];
  const summary = computeDailySummary(rows);

    const goToWorkout = (date) => {
    if (!date) return;
    navigate(`/workouts?date=${date}`);
  };

  return (
    <div className="bg-white rounded-xl shadow overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            <th className="px-3 py-2 text-center w-8"></th>
            <th className="px-3 py-2 text-left">{t('journal.col_date')}</th>
            <th className="px-3 py-2 text-left">{t('journal.col_type')}</th>
            <th className="px-3 py-2 text-center">{t('journal.col_rpe')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_planned_km')}</th>
            <th className="px-3 py-2 text-left">{t('journal.col_notes')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_actual_km')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_actual_pace')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_avg_hr')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_max_hr')}</th>
            <th className="px-3 py-2 text-right">{t('journal.col_elevation')}</th>
            <th className="px-3 py-2 text-right bg-orange-50">{t('journal.col_work_pace')}</th>
            <th className="px-3 py-2 text-right bg-orange-50">{t('journal.col_work_hr')}</th>
            <th className="px-3 py-2 text-right bg-blue-50">{t('journal.col_steady_pace')}</th>
            <th className="px-3 py-2 text-right bg-blue-50">{t('journal.col_steady_hr')}</th>
            <th className="px-3 py-2 text-left bg-green-50">{t('journal.col_supplementary')}</th>
          </tr>
        </thead>
        <tbody>
          {validRows.map((row) => {
            const isSelectable = row.date != null && row.actual?.distance_km != null;
            const isSelected = selectedDates?.includes(row.date);
            return (
              <tr key={row.date} className="border-t border-gray-100">
                <td className="px-3 py-2 text-center">
                  {isSelectable && (
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onToggleSelect(row.date)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                    />
                  )}
                </td>
                <td onClick={() => goToWorkout(row.date)} className="px-3 py-2 cursor-pointer hover:text-blue-600 hover:underline">
                  {row?.date ? format(parseISO(row.date), 'dd.MM EEE') : '—'}
                </td>
              <td
                onClick={() => goToWorkout(row.date)}
                className="px-3 py-2 cursor-pointer"
              >
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getWorkoutColor(row.workout_type)} hover:opacity-75`}>
                  {t(`workoutType.${row.workout_type}`)}
                </span>
              </td>
              <td className="px-3 py-2 text-center text-lg" title={row.rpe != null ? `RPE ${row.rpe}` : undefined}>
                {rpeEmoji(row.rpe) ?? '—'}
              </td>


              <td className="px-3 py-2 text-right">{row.planned?.distance_km ?? '—'}</td>
              <td className="px-3 py-2 text-left max-w-[200px] truncate">
                {row.planned?.notes?.split(';')[0] || '—'}
                {row.comment && (
                  <span className="ml-1 cursor-help" title={row.comment}>💬</span>
                )}
              </td>
              <td className="px-3 py-2 text-right">{row.actual?.distance_km ?? '—'}</td>
              <td className="px-3 py-2 text-right">{formatDecimalPace(row.actual?.avg_pace)}</td>
              <td className="px-3 py-2 text-right">{row.actual?.avg_hr != null ? Math.round(row.actual.avg_hr) : '—'}</td>
              <td className="px-3 py-2 text-right">{row.actual?.max_hr != null ? Math.round(row.actual.max_hr) : '—'}</td>
              <td className="px-3 py-2 text-right">{row.actual?.elevation_gain_m != null ? Math.round(row.actual.elevation_gain_m) : '—'}</td>
              <td className="px-3 py-2 text-right bg-orange-50">
                {row.work_summary ? formatDecimalPace(row.work_summary.avg_pace) : '—'}
              </td>
              <td className="px-3 py-2 text-right bg-orange-50">
                {row.work_summary?.avg_hr != null ? Math.round(row.work_summary.avg_hr) : '—'}
              </td>
              <td className="px-3 py-2 text-right bg-blue-50">
                {row.steady_summary ? formatDecimalPace(row.steady_summary.avg_pace) : '—'}
              </td>
              <td className="px-3 py-2 text-right bg-blue-50">
                {row.steady_summary?.avg_hr != null ? Math.round(row.steady_summary.avg_hr) : '—'}
              </td>
              <td className="px-3 py-2 text-left bg-green-50">
                {row.supplementary_activities?.length > 0
                  ? row.supplementary_activities.map((s, i) => (
                      <div key={i} className="text-xs text-gray-600 whitespace-nowrap">
                        +{s.distance_km}km @ {formatDecimalPace(s.avg_pace)}
                      </div>
                    ))
                  : '—'}
              </td>
            </tr>
            );
          })}
          {validRows.length === 0 && (
            <tr><td colSpan={16} className="px-3 py-6 text-center text-gray-400">{t('journal.no_data')}</td></tr>
          )}
        </tbody>
        {summary && (
          <tfoot>
            <tr className="border-t-2 font-semibold text-gray-700 bg-gray-50">
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2"></td>
                <td className="px-3 py-2 text-xs text-gray-500">{t('journal.total')}</td>
                <td className="px-3 py-2 text-center">
                  {summary.avgRpe != null ? summary.avgRpe.toFixed(1) : '—'}
                </td>

                <td className="px-3 py-2 text-right">{summary.plannedSum ?? '—'}</td>
                <td className="px-3 py-2 text-left">—</td>
                <td className="px-3 py-2 text-right">{summary.totalActualWithSupplementary ?? '—'}</td>
                <td className="px-3 py-2 text-right">{formatDecimalPace(summary.actualAvgPace)}</td>
                <td className="px-3 py-2 text-right">{summary.avgHr != null ? Math.round(summary.avgHr) : '—'}</td>
                <td className="px-3 py-2 text-right">{summary.maxHr != null ? Math.round(summary.maxHr) : '—'}</td>
                <td className="px-3 py-2 text-right">{summary.elevationSum ?? '—'}</td>
                <td className="px-3 py-2 text-right bg-orange-50">{formatDecimalPace(summary.workPace)}</td>
                <td className="px-3 py-2 text-right bg-orange-50">{summary.workHr != null ? Math.round(summary.workHr) : '—'}</td>
                <td className="px-3 py-2 text-right bg-blue-50">{formatDecimalPace(summary.steadyPace)}</td>
                <td className="px-3 py-2 text-right bg-blue-50">{summary.steadyHr != null ? Math.round(summary.steadyHr) : '—'}</td>
                <td className="px-3 py-2 text-left bg-green-50">
                  {summary.supplementarySum != null ? `+${summary.supplementarySum} km` : '—'}
                </td>
              </tr>
            </tfoot>
          )}

      </table>
    </div>
  );
}